import React, { useEffect, useMemo, useState } from "react";
import {
  createFullComparisonJob,
  createLookbookReportJob,
  downloadFullComparisonExcel,
  downloadLookbookExcel,
  getFullComparisonJobResult,
  getFullComparisonJobStatus,
  getLookbookJobResult,
  getLookbookJobStatus,
  getLookbookSeasonDefaults,
  getLookbookSeasons,
} from "../services/lookbookAnalyticsService";
import "./LookbookAnalyticsTool.css";

const POLL_INTERVAL_MS = 5000;

const todayIsoDate = () => new Date().toISOString().slice(0, 10);

const hasValue = (value) => value !== null && value !== undefined && value !== "";

const formatNumber = (value) =>
  Number(value || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });

const formatCurrency = (value) =>
  Number(value || 0).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  });

const formatPercent = (value) => `${Number(value || 0).toFixed(2)}%`;

const formatNullableNumber = (value) =>
  hasValue(value)
    ? Number(value).toLocaleString("en-US", { maximumFractionDigits: 0 })
    : "N/A";

const formatNullableCurrency = (value) =>
  hasValue(value)
    ? Number(value).toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 2,
      })
    : "N/A";

const formatNullablePercent = (value) =>
  hasValue(value) ? `${Number(value).toFixed(2)}%` : "N/A";

const getDownloadFilename = (response, fallback) => {
  const disposition = response.headers?.["content-disposition"] || "";
  const match = disposition.match(/filename="?([^"]+)"?/i);
  return match?.[1] || fallback;
};

const downloadBlobResponse = (response, fallbackFilename) => {
  const filename = getDownloadFilename(response, fallbackFilename);
  const blob = new Blob([response.data], {
    type:
      response.headers?.["content-type"] ||
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

function MetricTile({ label, value }) {
  return (
    <div className="lookbook-metric-tile">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function ChannelSplit({ title, data }) {
  return (
    <section className="lookbook-section">
      <div className="lookbook-section-header">
        <h3>{title}</h3>
      </div>
      <div className="lookbook-metric-grid compact">
        <MetricTile label="Mailed" value={formatNumber(data?.total_mailed)} />
        <MetricTile label="Redeemed" value={formatNumber(data?.total_redeemed)} />
        <MetricTile label="Redeemed %" value={formatPercent(data?.pct_redeemed)} />
        <MetricTile label="Net sales" value={formatCurrency(data?.net_sales)} />
        <MetricTile label="POS" value={formatNumber(data?.pos_count)} />
        <MetricTile label="WEB" value={formatNumber(data?.web_count)} />
        <MetricTile label="POS sales" value={formatCurrency(data?.pos_net_sales)} />
        <MetricTile label="WEB sales" value={formatCurrency(data?.web_net_sales)} />
      </div>
    </section>
  );
}

function ManagerCodesTable({ managerCodes }) {
  const codes = managerCodes?.codes || [];

  return (
    <section className="lookbook-section">
      <div className="lookbook-section-header">
        <h3>Manager Codes</h3>
      </div>
      <div className="lookbook-table-wrap">
        <table className="lookbook-table">
          <thead>
            <tr>
              <th>Code</th>
              <th>Uses</th>
              <th>Net Sales</th>
              <th>POS</th>
              <th>WEB</th>
            </tr>
          </thead>
          <tbody>
            {codes.map((code) => (
              <tr key={code.code}>
                <td>{code.code}</td>
                <td>{formatNumber(code.usage_count)}</td>
                <td>{formatCurrency(code.net_sales)}</td>
                <td>{formatNumber(code.pos_count)}</td>
                <td>{formatNumber(code.web_count)}</td>
              </tr>
            ))}
            <tr className="lookbook-total-row">
              <td>Total</td>
              <td>{formatNumber(managerCodes?.total_redeemed)}</td>
              <td>{formatCurrency(managerCodes?.total_net_sales)}</td>
              <td>{formatNumber(managerCodes?.pos_count)}</td>
              <td>{formatNumber(managerCodes?.web_count)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  );
}

function DailyBreakdownTable({ dailyBreakdown }) {
  const rows = useMemo(
    () =>
      Object.entries(dailyBreakdown || {}).map(([date, values]) => ({
        date,
        ...values,
      })),
    [dailyBreakdown]
  );

  if (rows.length === 0) {
    return null;
  }

  return (
    <section className="lookbook-section">
      <div className="lookbook-section-header">
        <h3>Daily Breakdown</h3>
      </div>
      <div className="lookbook-table-wrap tall">
        <table className="lookbook-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Total</th>
              <th>Physical</th>
              <th>Digital</th>
              <th>Net Sales</th>
              <th>POS</th>
              <th>WEB</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.date}>
                <td>{row.date}</td>
                <td>{formatNumber(row.total_redeemed)}</td>
                <td>{formatNumber(row.physical_redeemed)}</td>
                <td>{formatNumber(row.digital_redeemed)}</td>
                <td>{formatCurrency(row.total_net_sales)}</td>
                <td>{formatNumber(row.pos)}</td>
                <td>{formatNumber(row.web)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function ComparisonTable({ comparison }) {
  const rows = comparison?.rows || [];

  if (rows.length === 0) {
    return null;
  }

  return (
    <section className="lookbook-section">
      <div className="lookbook-section-header">
        <div>
          <h3>Full Season Comparison</h3>
          <span>Spring 2026 as of {comparison?.as_of_date}</span>
        </div>
      </div>
      <div className="lookbook-table-wrap comparison">
        <table className="lookbook-table comparison">
          <thead>
            <tr>
              <th>Season</th>
              <th>Total Coupons Sent</th>
              <th>Total Redeemed</th>
              <th>Total % Redeemed</th>
              <th>Total Sales (Net)</th>
              <th>AVG Order Total</th>
              <th>Total - Physical</th>
              <th>Physical % Redeemed</th>
              <th>Physical % POS</th>
              <th>Physical % WEB</th>
              <th>Total - Digital</th>
              <th>Digital % Redeemed</th>
              <th>Digital % POS</th>
              <th>Digital % WEB</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const physical = row.physical || {};
              const digital = row.digital || {};
              const seasonLabel =
                row.status === "season_to_date" && row.as_of_date
                  ? `${row.season} as of ${row.as_of_date}`
                  : row.season;

              return (
                <tr key={`${row.season}-${row.status}`}>
                  <td>{seasonLabel}</td>
                  <td>{formatNullableNumber(row.mailed)}</td>
                  <td>{formatNullableNumber(row.redeemed)}</td>
                  <td>{formatNullablePercent(row.pct_redeemed)}</td>
                  <td>{formatNullableCurrency(row.net_sales)}</td>
                  <td>{formatNullableCurrency(row.avg_order)}</td>
                  <td>{formatNullableNumber(physical.mailed)}</td>
                  <td>{formatNullablePercent(physical.pct_redeemed)}</td>
                  <td>{formatNullablePercent(physical.pos_pct)}</td>
                  <td>{formatNullablePercent(physical.web_pct)}</td>
                  <td>{formatNullableNumber(digital.mailed)}</td>
                  <td>{formatNullablePercent(digital.pct_redeemed)}</td>
                  <td>{formatNullablePercent(digital.pos_pct)}</td>
                  <td>{formatNullablePercent(digital.web_pct)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function LookbookAnalyticsTool() {
  const [activeTab, setActiveTab] = useState("current");
  const [seasons, setSeasons] = useState([]);
  const [season, setSeason] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [loadingSeasons, setLoadingSeasons] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [job, setJob] = useState(null);
  const [report, setReport] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState("");
  const [statusWarning, setStatusWarning] = useState("");
  const [comparisonGroup, setComparisonGroup] = useState("all");
  const [comparisonAsOfDate, setComparisonAsOfDate] = useState(todayIsoDate());
  const [comparisonGenerating, setComparisonGenerating] = useState(false);
  const [comparisonJob, setComparisonJob] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [comparisonDownloading, setComparisonDownloading] = useState(false);
  const [comparisonError, setComparisonError] = useState("");
  const [comparisonStatusWarning, setComparisonStatusWarning] = useState("");

  const isJobActive = job?.job_id && ["queued", "running"].includes(job.status);
  const isComparisonJobActive =
    comparisonJob?.job_id && ["queued", "running"].includes(comparisonJob.status);

  useEffect(() => {
    let cancelled = false;

    const loadSeasons = async () => {
      setLoadingSeasons(true);
      setError("");
      try {
        const data = await getLookbookSeasons();
        if (cancelled) return;
        const availableSeasons = data.seasons || [];
        setSeasons(availableSeasons);
        if (availableSeasons.length > 0) {
          setSeason(availableSeasons[0]);
        }
      } catch (_err) {
        if (!cancelled) {
          setError("Unable to load Lookbook seasons.");
        }
      } finally {
        if (!cancelled) {
          setLoadingSeasons(false);
        }
      }
    };

    loadSeasons();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!season) return undefined;

    let cancelled = false;

    const loadDefaults = async () => {
      try {
        const data = await getLookbookSeasonDefaults(season);
        if (cancelled) return;
        setDateFrom(data.start_date || "");
        setDateTo(data.end_date || "");
      } catch (_err) {
        if (!cancelled) {
          setError("Unable to load the default date range.");
        }
      }
    };

    loadDefaults();

    return () => {
      cancelled = true;
    };
  }, [season]);

  useEffect(() => {
    if (!isJobActive) return undefined;

    let cancelled = false;
    let missedPolls = 0;

    const pollJob = async () => {
      try {
        const status = await getLookbookJobStatus(job.job_id);
        if (cancelled) return;
        missedPolls = 0;
        setStatusWarning("");
        setJob(status);

        if (status.status === "completed") {
          setGenerating(false);
        } else if (status.status === "failed") {
          setError(status.error || "Lookbook report generation failed.");
          setGenerating(false);
        }
      } catch (_err) {
        if (!cancelled) {
          missedPolls += 1;
          setStatusWarning(
            missedPolls === 1
              ? "Status check failed once. Retrying automatically."
              : `Status check failed ${missedPolls} times. Retrying automatically.`
          );
        }
      }
    };

    pollJob();
    const intervalId = window.setInterval(pollJob, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [isJobActive, job?.job_id]);

  useEffect(() => {
    if (!job?.job_id || job.status !== "completed" || report) return undefined;

    let cancelled = false;

    const loadCompletedReport = async () => {
      try {
        const result = await getLookbookJobResult(job.job_id);
        if (cancelled) return;
        setJob(result);
        setReport(result.result);
        setGenerating(false);
      } catch (_err) {
        if (!cancelled) {
          setError("Unable to load the completed Lookbook report.");
          setGenerating(false);
        }
      }
    };

    loadCompletedReport();

    return () => {
      cancelled = true;
    };
  }, [job?.job_id, job?.status, report]);

  useEffect(() => {
    if (!isComparisonJobActive) return undefined;

    let cancelled = false;
    let missedPolls = 0;

    const pollComparisonJob = async () => {
      try {
        const status = await getFullComparisonJobStatus(comparisonJob.job_id);
        if (cancelled) return;
        missedPolls = 0;
        setComparisonStatusWarning("");
        setComparisonJob(status);

        if (status.status === "completed") {
          setComparisonGenerating(false);
        } else if (status.status === "failed") {
          setComparisonError(status.error || "Lookbook comparison generation failed.");
          setComparisonGenerating(false);
        }
      } catch (_err) {
        if (!cancelled) {
          missedPolls += 1;
          setComparisonStatusWarning(
            missedPolls === 1
              ? "Status check failed once. Retrying automatically."
              : `Status check failed ${missedPolls} times. Retrying automatically.`
          );
        }
      }
    };

    pollComparisonJob();
    const intervalId = window.setInterval(pollComparisonJob, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [comparisonJob?.job_id, isComparisonJobActive]);

  useEffect(() => {
    if (!comparisonJob?.job_id || comparisonJob.status !== "completed" || comparison) {
      return undefined;
    }

    let cancelled = false;

    const loadCompletedComparison = async () => {
      try {
        const result = await getFullComparisonJobResult(comparisonJob.job_id);
        if (cancelled) return;
        setComparisonJob(result);
        setComparison(result.result);
        setComparisonGenerating(false);
      } catch (_err) {
        if (!cancelled) {
          setComparisonError("Unable to load the completed Lookbook comparison.");
          setComparisonGenerating(false);
        }
      }
    };

    loadCompletedComparison();

    return () => {
      cancelled = true;
    };
  }, [comparison, comparisonJob?.job_id, comparisonJob?.status]);

  const handleGenerate = async () => {
    setError("");
    setStatusWarning("");
    setReport(null);
    setJob(null);
    setGenerating(true);

    try {
      const createdJob = await createLookbookReportJob({
        season,
        date_from: dateFrom,
        date_to: dateTo,
      });
      setJob(createdJob);
    } catch (err) {
      setError(err.response?.data?.error || "Unable to start Lookbook report.");
      setGenerating(false);
    }
  };

  const handleDownloadExcel = async () => {
    if (!job?.job_id) return;

    setDownloading(true);
    setError("");

    try {
      const response = await downloadLookbookExcel(job.job_id);
      downloadBlobResponse(response, `lookbook_analytics_${season || "report"}.xlsx`);
    } catch (_err) {
      setError("Unable to download the Excel report.");
    } finally {
      setDownloading(false);
    }
  };

  const handleGenerateComparison = async () => {
    setComparisonError("");
    setComparisonStatusWarning("");
    setComparison(null);
    setComparisonJob(null);
    setComparisonGenerating(true);

    try {
      const createdJob = await createFullComparisonJob({
        as_of_date: comparisonAsOfDate,
        season_group: comparisonGroup,
      });
      setComparisonJob(createdJob);
    } catch (err) {
      setComparisonError(err.response?.data?.error || "Unable to start Lookbook comparison.");
      setComparisonGenerating(false);
    }
  };

  const handleDownloadComparisonExcel = async () => {
    if (!comparisonJob?.job_id) return;

    setComparisonDownloading(true);
    setComparisonError("");

    try {
      const response = await downloadFullComparisonExcel(comparisonJob.job_id);
      downloadBlobResponse(
        response,
        `lookbook_season_comparison_${comparison?.as_of_date || "current"}.xlsx`
      );
    } catch (_err) {
      setComparisonError("Unable to download the comparison Excel report.");
    } finally {
      setComparisonDownloading(false);
    }
  };

  const summary = report?.summary;

  return (
    <div className="lookbook-wrapper">
      <div className="lookbook-header">
        <h2>Lookbook Analytics</h2>
      </div>

      <div className="lookbook-tabs" role="tablist" aria-label="Lookbook views">
        <button
          type="button"
          className={activeTab === "current" ? "active" : ""}
          onClick={() => setActiveTab("current")}
        >
          Current Season
        </button>
        <button
          type="button"
          className={activeTab === "comparison" ? "active" : ""}
          onClick={() => setActiveTab("comparison")}
        >
          Season Comparison
        </button>
      </div>

      {activeTab === "current" && (
        <>
          <section className="lookbook-controls">
            <div className="lookbook-field">
              <label>Season</label>
              <select
                value={season}
                onChange={(event) => setSeason(event.target.value)}
                disabled={loadingSeasons || generating}
              >
                {seasons.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </div>

            <div className="lookbook-field">
              <label>Date From</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(event) => setDateFrom(event.target.value)}
                disabled={generating}
              />
            </div>

            <div className="lookbook-field">
              <label>Date To</label>
              <input
                type="date"
                value={dateTo}
                onChange={(event) => setDateTo(event.target.value)}
                disabled={generating}
              />
            </div>

            <button
              type="button"
              className="lookbook-run-button"
              onClick={handleGenerate}
              disabled={!season || !dateFrom || !dateTo || loadingSeasons || generating}
            >
              {generating ? "Running..." : "Generate Report"}
            </button>
          </section>

          {error && <div className="lookbook-alert error">{error}</div>}
          {statusWarning && <div className="lookbook-alert warning">{statusWarning}</div>}

          {job && !report && !error && (
            <section className="lookbook-job-status">
              <span>Status</span>
              <strong>{job.status}</strong>
              <small>{job.job_id}</small>
            </section>
          )}

          {report && (
            <div className="lookbook-report">
              <section className="lookbook-summary">
                <div className="lookbook-section-header">
                  <div>
                    <h3>{report.meta?.season}</h3>
                    <span>
                      {report.meta?.date_from} to {report.meta?.date_to}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="lookbook-download-button"
                    onClick={handleDownloadExcel}
                    disabled={downloading || !job?.job_id}
                  >
                    {downloading ? "Downloading..." : "Download Excel"}
                  </button>
                </div>
                <div className="lookbook-metric-grid">
                  <MetricTile label="Total mailed" value={formatNumber(summary?.total_mailed)} />
                  <MetricTile
                    label="Total redeemed"
                    value={formatNumber(summary?.total_redeemed)}
                  />
                  <MetricTile
                    label="Redeemed %"
                    value={formatPercent(summary?.total_pct_redeemed)}
                  />
                  <MetricTile label="Net sales" value={formatCurrency(summary?.total_net_sales)} />
                  <MetricTile
                    label="Avg sale/coupon"
                    value={formatCurrency(summary?.avg_sale_per_coupon)}
                  />
                </div>
              </section>

              <div className="lookbook-split-grid">
                <ChannelSplit title="Physical" data={report.physical} />
                <ChannelSplit title="Digital" data={report.digital} />
              </div>

              <ManagerCodesTable managerCodes={report.manager_codes} />
              <DailyBreakdownTable dailyBreakdown={report.daily_breakdown} />
            </div>
          )}
        </>
      )}

      {activeTab === "comparison" && (
        <>
          <section className="lookbook-controls comparison">
            <div className="lookbook-field">
              <label>Comparison</label>
              <select value="full-season" disabled>
                <option value="full-season">Full Season Comparison</option>
              </select>
            </div>

            <div className="lookbook-field">
              <label>Season Group</label>
              <select
                value={comparisonGroup}
                onChange={(event) => setComparisonGroup(event.target.value)}
                disabled={comparisonGenerating}
              >
                <option value="all">All</option>
                <option value="spring">Spring</option>
                <option value="fall">Fall</option>
              </select>
            </div>

            <div className="lookbook-field">
              <label>As Of Date</label>
              <input
                type="date"
                value={comparisonAsOfDate}
                onChange={(event) => setComparisonAsOfDate(event.target.value)}
                disabled={comparisonGenerating}
              />
            </div>

            <button
              type="button"
              className="lookbook-run-button"
              onClick={handleGenerateComparison}
              disabled={!comparisonAsOfDate || comparisonGenerating}
            >
              {comparisonGenerating ? "Running..." : "Generate Comparison"}
            </button>
          </section>

          {comparisonError && <div className="lookbook-alert error">{comparisonError}</div>}
          {comparisonStatusWarning && (
            <div className="lookbook-alert warning">{comparisonStatusWarning}</div>
          )}

          {comparisonJob && !comparison && !comparisonError && (
            <section className="lookbook-job-status">
              <span>Status</span>
              <strong>{comparisonJob.status}</strong>
              <small>{comparisonJob.job_id}</small>
            </section>
          )}

          {comparison && (
            <div className="lookbook-report">
              <section className="lookbook-summary">
                <div className="lookbook-section-header">
                  <div>
                    <h3>Season Comparison</h3>
                    <span>As of {comparison.as_of_date}</span>
                  </div>
                  <button
                    type="button"
                    className="lookbook-download-button"
                    onClick={handleDownloadComparisonExcel}
                    disabled={comparisonDownloading || !comparisonJob?.job_id}
                  >
                    {comparisonDownloading ? "Downloading..." : "Download Excel"}
                  </button>
                </div>
              </section>

              <ComparisonTable comparison={comparison} />
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default LookbookAnalyticsTool;
